/**
 * Client-side Supabase queries for blog
 * These run in the browser for CSR on S3 static hosting
 */

import { createClient } from "./client"
import type { BlogPost, TopicTag } from "@/types/blog"

// Get all published posts with tags
export async function getPublishedPosts(limit?: number): Promise<BlogPost[]> {
  const supabase = createClient()

  const { data: posts, error } = await supabase
    .from("blog_posts")
    .select("*")
    .eq("published", true)
    .order("published_at", { ascending: false })
    .limit(limit ?? 100)

  if (error) {
    console.error("Error fetching posts:", error)
    return []
  }

  // Get tags for each post
  const postsWithTags = await Promise.all(
    posts.map(async (post) => {
      const { data: tagRows } = await supabase
        .from("blog_post_tags")
        .select("tag_id, blog_tags(name)")
        .eq("post_id", post.id)

      const tags = tagRows?.map((t) => {
        const blogTags = t.blog_tags as { name: string } | { name: string }[] | null
        if (Array.isArray(blogTags)) {
          return blogTags[0]?.name
        }
        return blogTags?.name
      }).filter((name): name is string => !!name) ?? []

      return transformPost(post, tags)
    })
  )

  return postsWithTags
}

// Get a single post by slug
export async function getPostBySlug(slug: string): Promise<BlogPost | null> {
  const supabase = createClient()

  const { data: post, error } = await supabase
    .from("blog_posts")
    .select("*")
    .eq("slug", slug)
    .eq("published", true)
    .single()

  if (error || !post) {
    console.error("Error fetching post:", error)
    return null
  }

  const { data: tagRows } = await supabase
    .from("blog_post_tags")
    .select("tag_id, blog_tags(name)")
    .eq("post_id", post.id)

  const tags = tagRows?.map((t) => {
    const blogTags = t.blog_tags as { name: string } | { name: string }[] | null
    if (Array.isArray(blogTags)) {
      return blogTags[0]?.name
    }
    return blogTags?.name
  }).filter((name): name is string => !!name) ?? []

  return transformPost(post, tags)
}

// Get all tags with post count
export async function getAllTags(): Promise<TopicTag[]> {
  const supabase = createClient()

  const { data: tags, error } = await supabase
    .from("blog_tags")
    .select("name, slug")
    .order("name")

  if (error || !tags) {
    console.error("Error fetching tags:", error)
    return []
  }

  // Get count for each tag (only published posts)
  const tagsWithCount = await Promise.all(
    tags.map(async (tag) => {
      // First get the tag id
      const { data: tagData } = await supabase
        .from("blog_tags")
        .select("id")
        .eq("slug", tag.slug)
        .single()

      if (!tagData) {
        return { name: tag.name, slug: tag.slug, count: 0 }
      }

      // Count posts with this tag that are published
      const { data: postTags } = await supabase
        .from("blog_post_tags")
        .select("post_id, blog_posts!inner(published)")
        .eq("tag_id", tagData.id)
        .eq("blog_posts.published", true)

      return {
        name: tag.name,
        slug: tag.slug,
        count: postTags?.length ?? 0,
      }
    })
  )

  return tagsWithCount
}

// Search posts by query and optional topic filter
export async function searchPosts(
  query: string,
  topic: string | null
): Promise<BlogPost[]> {
  const supabase = createClient()

  let queryBuilder = supabase
    .from("blog_posts")
    .select("*")
    .eq("published", true)
    .order("published_at", { ascending: false })

  if (query) {
    // Search in title, excerpt, and content
    queryBuilder = queryBuilder.or(
      `title.ilike.%${query}%,excerpt.ilike.%${query}%,content.ilike.%${query}%`
    )
  }

  const { data: posts, error } = await queryBuilder

  if (error || !posts) {
    console.error("Error searching posts:", error)
    return []
  }

  // Get tags for each post and filter by topic if needed
  const postsWithTags = await Promise.all(
    posts.map(async (post) => {
      const { data: tagRows } = await supabase
        .from("blog_post_tags")
        .select("tag_id, blog_tags(name, slug)")
        .eq("post_id", post.id)

      const tags = tagRows?.map((t) => {
        const blogTags = t.blog_tags as { name: string; slug: string } | { name: string; slug: string }[] | null
        if (Array.isArray(blogTags)) {
          return blogTags[0]
        }
        return blogTags
      }).filter((tag): tag is { name: string; slug: string } => !!tag) ?? []

      const tagNames = tags.map((t) => t.name)
      const tagSlugs = tags.map((t) => t.slug)

      return {
        post,
        tags: tagNames,
        tagSlugs,
      }
    })
  )

  // Filter by topic if specified
  let filtered = postsWithTags
  if (topic) {
    filtered = postsWithTags.filter(({ tagSlugs }) => tagSlugs.includes(topic))
  }

  return filtered.map(({ post, tags }) => transformPost(post, tags))
}

// Transform database row to BlogPost format
function transformPost(
  post: {
    id: string
    slug: string
    title: string
    excerpt: string
    content: string
    author: string | null
    published_at: string | null
    created_at: string
    updated_at: string | null
  },
  tags: string[]
): BlogPost {
  return {
    id: post.id,
    slug: post.slug,
    title: post.title,
    excerpt: post.excerpt,
    content: post.content,
    author: post.author ?? undefined,
    publishedAt: post.published_at ? new Date(post.published_at) : new Date(post.created_at),
    updatedAt: post.updated_at ? new Date(post.updated_at) : undefined,
    tags,
  }
}

// ============ Admin Queries ============

interface BlogPostWithTagsAdmin {
  id: string
  slug: string
  title: string
  excerpt: string
  content: string
  author: string | null
  published: boolean
  publishedAt: Date | null
  createdAt: Date
  updatedAt: Date | null
  tags: { id: string; name: string; slug: string; createdAt: Date }[]
}

interface BlogTagAdmin {
  id: string
  name: string
  slug: string
  createdAt: Date
}

// Get all posts for admin (including unpublished)
export async function getAllPostsForAdmin(): Promise<BlogPostWithTagsAdmin[]> {
  const supabase = createClient()

  const { data: posts, error } = await supabase
    .from("blog_posts")
    .select("*")
    .order("created_at", { ascending: false })

  if (error || !posts) {
    console.error("Error fetching admin posts:", error)
    return []
  }

  const postsWithTags = await Promise.all(
    posts.map(async (post) => {
      const { data: tagRows } = await supabase
        .from("blog_post_tags")
        .select("tag_id, blog_tags(id, name, slug, created_at)")
        .eq("post_id", post.id)

      const tags = tagRows?.map((t) => {
        const blogTags = t.blog_tags as { id: string; name: string; slug: string; created_at: string } | { id: string; name: string; slug: string; created_at: string }[] | null
        if (Array.isArray(blogTags)) {
          return blogTags[0]
        }
        return blogTags
      }).filter((tag): tag is { id: string; name: string; slug: string; created_at: string } => !!tag) ?? []

      return {
        id: post.id,
        slug: post.slug,
        title: post.title,
        excerpt: post.excerpt,
        content: post.content,
        author: post.author,
        published: post.published,
        publishedAt: post.published_at ? new Date(post.published_at) : null,
        createdAt: new Date(post.created_at),
        updatedAt: post.updated_at ? new Date(post.updated_at) : null,
        tags: tags.map((tag) => ({
          id: tag.id,
          name: tag.name,
          slug: tag.slug,
          createdAt: new Date(tag.created_at),
        })),
      }
    })
  )

  return postsWithTags
}

// Get all tags for admin
export async function getAllTagsForAdmin(): Promise<BlogTagAdmin[]> {
  const supabase = createClient()

  const { data: tags, error } = await supabase
    .from("blog_tags")
    .select("*")
    .order("name")

  if (error || !tags) {
    console.error("Error fetching admin tags:", error)
    return []
  }

  return tags.map((tag) => ({
    id: tag.id,
    name: tag.name,
    slug: tag.slug,
    createdAt: new Date(tag.created_at),
  }))
}

// Get post by ID for admin
export async function getPostByIdForAdmin(id: string): Promise<BlogPostWithTagsAdmin | null> {
  const supabase = createClient()

  const { data: post, error } = await supabase
    .from("blog_posts")
    .select("*")
    .eq("id", id)
    .single()

  if (error || !post) {
    console.error("Error fetching admin post:", error)
    return null
  }

  const { data: tagRows } = await supabase
    .from("blog_post_tags")
    .select("tag_id, blog_tags(id, name, slug, created_at)")
    .eq("post_id", post.id)

  const tags = tagRows?.map((t) => {
    const blogTags = t.blog_tags as { id: string; name: string; slug: string; created_at: string } | { id: string; name: string; slug: string; created_at: string }[] | null
    if (Array.isArray(blogTags)) {
      return blogTags[0]
    }
    return blogTags
  }).filter((tag): tag is { id: string; name: string; slug: string; created_at: string } => !!tag) ?? []

  return {
    id: post.id,
    slug: post.slug,
    title: post.title,
    excerpt: post.excerpt,
    content: post.content,
    author: post.author,
    published: post.published,
    publishedAt: post.published_at ? new Date(post.published_at) : null,
    createdAt: new Date(post.created_at),
    updatedAt: post.updated_at ? new Date(post.updated_at) : null,
    tags: tags.map((tag) => ({
      id: tag.id,
      name: tag.name,
      slug: tag.slug,
      createdAt: new Date(tag.created_at),
    })),
  }
}

// ============ Admin Mutations ============

export interface CreatePostInput {
  title: string
  slug: string
  excerpt: string
  content: string
  author?: string
  tags: string[] // tag IDs
  published?: boolean
}

export interface UpdatePostInput extends Partial<CreatePostInput> {
  id: string
}

// Create a new post
export async function createPost(
  input: CreatePostInput
): Promise<{ success: boolean; error?: string; id?: string }> {
  const supabase = createClient()

  const { data: post, error } = await supabase
    .from("blog_posts")
    .insert({
      title: input.title,
      slug: input.slug,
      excerpt: input.excerpt,
      content: input.content,
      author: input.author || null,
      published: input.published || false,
      published_at: input.published ? new Date().toISOString() : null,
    })
    .select()
    .single()

  if (error || !post) {
    console.error("Error creating post:", error)
    return { success: false, error: error?.message || "Failed to create post" }
  }

  // Link tags to the post
  if (input.tags.length > 0) {
    const { error: tagError } = await supabase
      .from("blog_post_tags")
      .insert(input.tags.map((tagId) => ({ post_id: post.id, tag_id: tagId })))

    if (tagError) {
      console.error("Error linking tags:", tagError)
    }
  }

  return { success: true, id: post.id }
}

// Update an existing post
export async function updatePost(
  input: UpdatePostInput
): Promise<{ success: boolean; error?: string }> {
  const supabase = createClient()

  const updateData: Record<string, unknown> = {
    updated_at: new Date().toISOString(),
  }

  if (input.title !== undefined) updateData.title = input.title
  if (input.slug !== undefined) updateData.slug = input.slug
  if (input.excerpt !== undefined) updateData.excerpt = input.excerpt
  if (input.content !== undefined) updateData.content = input.content
  if (input.author !== undefined) updateData.author = input.author || null

  if (input.published !== undefined) {
    updateData.published = input.published
    // Set published_at when publishing for the first time
    if (input.published) {
      const { data: existingPost } = await supabase
        .from("blog_posts")
        .select("published_at")
        .eq("id", input.id)
        .single()

      if (!existingPost?.published_at) {
        updateData.published_at = new Date().toISOString()
      }
    }
  }

  const { error } = await supabase
    .from("blog_posts")
    .update(updateData)
    .eq("id", input.id)

  if (error) {
    console.error("Error updating post:", error)
    return { success: false, error: error.message }
  }

  // Update tags if provided
  if (input.tags !== undefined) {
    // Remove existing tag links
    await supabase.from("blog_post_tags").delete().eq("post_id", input.id)

    // Add new tag links
    if (input.tags.length > 0) {
      const { error: tagError } = await supabase
        .from("blog_post_tags")
        .insert(input.tags.map((tagId) => ({ post_id: input.id, tag_id: tagId })))

      if (tagError) {
        console.error("Error linking tags:", tagError)
      }
    }
  }

  return { success: true }
}

// Delete a post
export async function deletePost(
  id: string
): Promise<{ success: boolean; error?: string }> {
  const supabase = createClient()

  // Delete tag links first
  await supabase.from("blog_post_tags").delete().eq("post_id", id)

  // Delete the post
  const { error } = await supabase
    .from("blog_posts")
    .delete()
    .eq("id", id)

  if (error) {
    console.error("Error deleting post:", error)
    return { success: false, error: error.message }
  }

  return { success: true }
}

// Create a new tag
export async function createTag(
  input: { name: string; slug: string }
): Promise<{ success: boolean; error?: string; id?: string }> {
  const supabase = createClient()

  const { data: tag, error } = await supabase
    .from("blog_tags")
    .insert({ name: input.name, slug: input.slug })
    .select()
    .single()

  if (error || !tag) {
    console.error("Error creating tag:", error)
    if (error?.message.includes("unique") || error?.message.includes("duplicate")) {
      return { success: false, error: "A tag with this slug already exists" }
    }
    return { success: false, error: error?.message || "Failed to create tag" }
  }

  return { success: true, id: tag.id }
}

// Update a tag
export async function updateTag(
  input: { id: string; name?: string; slug?: string }
): Promise<{ success: boolean; error?: string }> {
  const supabase = createClient()

  const updateData: Record<string, unknown> = {}
  if (input.name !== undefined) updateData.name = input.name
  if (input.slug !== undefined) updateData.slug = input.slug

  const { error } = await supabase
    .from("blog_tags")
    .update(updateData)
    .eq("id", input.id)

  if (error) {
    console.error("Error updating tag:", error)
    if (error.message.includes("unique") || error.message.includes("duplicate")) {
      return { success: false, error: "A tag with this slug already exists" }
    }
    return { success: false, error: error.message }
  }

  return { success: true }
}

// Delete a tag
export async function deleteTag(
  id: string
): Promise<{ success: boolean; error?: string }> {
  const supabase = createClient()

  // Delete tag links first
  await supabase.from("blog_post_tags").delete().eq("tag_id", id)

  // Delete the tag
  const { error } = await supabase
    .from("blog_tags")
    .delete()
    .eq("id", id)

  if (error) {
    console.error("Error deleting tag:", error)
    return { success: false, error: error.message }
  }

  return { success: true }
}
